<script lang="ts" setup>
import type { RefresherCustomEvent } from '@ionic/vue';
import { useTemplateRef } from 'vue';

const debtorAPI = useDebtorAPI();
const userConfigurationStore = useUserConfigurationStore();

const { usingTouch } = useInputTracker();

const { openToast } = useUserMessages();
const { t } = useEkI18n();

const createModal = useTemplateRef('createModal');
const searchbar = useTemplateRef('searchbar');

const searchTerm = ref('');
const customerIsOpen = ref(false);

const pending = computed(() => status.value === 'pending');

const { data: results, status, refresh } = useAsyncData( 'ListViewSearchDebtors', searchDebtors, { immediate: false, default: () => [], watch: [searchTerm] } );
const { window, windowSize, isAllLoaded, incrementCount, resetWindow } = useArrayWindow(results, { incrementCount: 10 });

const searchMessage = computed(() => {
  switch (true) {
    case (searchTerm.value.length < 3):
      return t('search.minCharacters', { count: 3 });
    case (searchTerm.value.length >= 3 && results.value?.length === 0 && status.value !== 'pending'):
      return t('ekomplet.debtorNotFound', 1);
    case (status.value === 'pending'):
      return t('search.searching');
    default:
      return '';
  }
});

async function handleSavedDebtor(debtorId: number) {
  const response = await debtorAPI.getDebtorByID(debtorId);

  if (response === undefined || response?.HasErrors !== false) {
    openToast(t('alert.errorOccurred'), ToastType.ERROR);

    return [];
  }

  results.value = [response.Data];
  resetWindow();
}

async function searchDebtors() : Promise< AppSearchDebtor[]> {
  if (searchTerm.value.length < 3) {
    return [];
  }

  const response = await debtorAPI.searchDebtors(searchTerm.value);

  if (response === undefined || response?.HasErrors !== false) {
    openToast(t('alert.errorOccurred'), ToastType.ERROR);

    return [];
  }

  return response.Data;
}

function handleRefresh(event: RefresherCustomEvent) {
  refresh();
  resetWindow();
  event.target.complete();
}

function openCustomers() {
  customerIsOpen.value = true;
}

defineExpose({ openCustomers });
</script>

<template>
  <Modal
    v-model:open="customerIsOpen"
    :title="t('worksheet.customer', 2)"
    animation-direction="vertical"
    @did-present="searchbar?.$el.setFocus();"
  >
    <IonToolbar>
      <IonSearchbar
        ref="searchbar"
        v-model="searchTerm"
        color="card-background"
        :placeholder="t('search.customer')"
        autocomplete="on"
        :debounce="500"
        @ion-clear="resetWindow"
      />
      <IonProgressBar
        v-show="pending"
        type="indeterminate"
      />
    </IonToolbar>
    <IonContent>
      <IonRefresher
        slot="fixed"
        close-duration="1000ms"
        :disabled="!usingTouch"
        @ion-refresh="handleRefresh($event)"
      >
        <IonRefresherContent />
      </IonRefresher>
      <IonContent>
        <span
          v-if="searchMessage.length > 0"
          class="searchMessage"
        >
          {{ searchMessage }}
        </span>
        <IonList>
          <CustomersInfoCard
            v-for="customer in window"
            :key="customer.ID"
            :item="customer"
            :active-button="false"
          />
          <InputInfiniteScroll
            v-model="windowSize"
            :disabled="isAllLoaded"
            :increment-count="incrementCount"
          />
        </IonList>
      </IonContent>
      <ButtonsFloatingActionButton
        v-if="userConfigurationStore.canCreateDebtor"
        :tooltip="t('customer.createCustomer')"
        @click="createModal?.open()"
      />
    </IonContent>
  </Modal>
  <CustomersCreateModal
    ref="createModal"
    @created-customer="handleSavedDebtor"
  />
</template>

<style scoped>
ion-searchbar {
  --border-radius: 100px;
}

.searchMessage {
    padding: 0.2rem;
    font-size: small;
    color: var(--ion-color-medium);
    display: flex;
    justify-content: center;
  }
</style>
